import { useCallback, useEffect, useRef, useState } from "react";
import { debounce, isEqual } from "lodash";

import useQuery from "hooks/useQuery";
import { SearchQueryObj } from "types/search";
import isInputEmpty from "utils/isInputEmpty";
import { history } from "config/history";
import { useDispatch } from "react-redux";
import { SearchBody } from "api-client/models/SearchBody";
import { search, searchMore, setQuery } from "actions/collection";
import useSelector from "hooks/useSelector";

const DEFAULT_SEARCH_LIMIT = 10;

function buildSearchQueryObjFromSearchQuery(query: URLSearchParams): SearchQueryObj {
    // only for the UI to keep track of changes to decide when to perform a new search
    const freeText = getSearchOptionsFromQuery(query);
    const queryObj: SearchQueryObj = {
        freeText: freeText,
    };
    return queryObj;
}

export function buildSearchQueryPayloadFromSearchQuery(
    query: URLSearchParams,
    offset: number,
    limit: number,
): SearchBody {
    const freeText = getSearchOptionsFromQuery(query);

    const queryPayload: SearchBody = {
        freeText: freeText,
        offset: offset,
        limit: limit,
    };

    return queryPayload;
}

export function useCollectionSearchQueryChange() {
    const mounted = useRef(false);
    const query = useQuery();
    const dispatch = useDispatch();
    const lastSearchQuery = useSelector((state) => state.collection.searchOptions.queryObj);
    useEffect(() => {
        const searchQueryObj = buildSearchQueryObjFromSearchQuery(query);
        if (!isEqual(lastSearchQuery, searchQueryObj) && searchQueryObj.freeText !== "") {
            dispatch(
                search(buildSearchQueryPayloadFromSearchQuery(query, 0, DEFAULT_SEARCH_LIMIT)),
            );
            if (!mounted.current) {
                // Going to a stories page, scroll to the bottom, and go back to the collection.
                // it will try to restore the previous scroll position in the stroy page, as a result
                // infinite scrolling fetches everything up until that point which is buggy and jumpy.
                // so we need to restore the scroll position back to 0 on the very first navigation if
                // there's something to fetch(if we have not caching the previous results in redux).
                window.scrollTo(0, 0);
            }
        }
        if (!mounted.current) {
            mounted.current = true;
        }
        dispatch(setQuery(searchQueryObj));
    }, [query.toString()]);
}

function getSearchOptionsFromQuery(query: URLSearchParams) {
    const freeText = query.get("freeText") || "";
    return freeText;
}

export function useFreeText(): [string, (_: string) => void] {
    const query = useQuery();
    const freeText = getSearchOptionsFromQuery(query);
    const [mirrorFreeText, setMirrorFreeText] = useState(freeText);
    const updateSearchQueryInUrl = useCallback(
        debounce((q: string) => {
            // update the query in the url later
            // so that the UI doesn't get blocked
            history.replace({
                search: q,
            });
        }, 1000),
        [],
    );
    const onFreeTextChange = (value: string) => {
        setMirrorFreeText(value);
    };
    useEffect(() => {
        if (mirrorFreeText !== freeText) {
            if (!isInputEmpty(mirrorFreeText)) {
                query.set("freeText", mirrorFreeText);
            } else {
                query.delete("freeText");
            }
            updateSearchQueryInUrl(query.toString());
        }
    }, [mirrorFreeText]);

    useEffect(() => {
        if (freeText !== mirrorFreeText) {
            setMirrorFreeText(freeText);
        }
    }, [freeText]);

    return [mirrorFreeText, onFreeTextChange];
}

export function useLoadMore() {
    const query = useQuery();
    const dispatch = useDispatch();
    const loadingMore = useSelector((state) => state.collection.searchResults.loadingMore);
    const hasMore = useSelector((state) => state.collection.searchResults.results?.hasMore);
    const currentOffset = useSelector(
        (state) => state.collection.searchResults.results?.offset || 0,
    );
    const nextOffset = currentOffset + DEFAULT_SEARCH_LIMIT;
    function loadMore() {
        if (!hasMore || loadingMore) {
            return;
        }
        const searchQueryObj = buildSearchQueryPayloadFromSearchQuery(
            query,
            nextOffset,
            DEFAULT_SEARCH_LIMIT,
        );
        dispatch(searchMore(searchQueryObj));
    }
    const memorizedLoadmore = useCallback(() => {
        loadMore();
        // this is placed in the result component, as a result everytime a query param changes, it will be re-created and then re-renders the entire result list as well.
        // so we memorize it, and it should only re-render when needed.
    }, [hasMore, nextOffset, loadingMore]);
    return memorizedLoadmore;
}
